<template>
    <div class="message-create-container">
        <el-card class="message-card" shadow="hover">
            <template #header>
                <div class="card-header">
                    <el-icon>
                        <Message />
                    </el-icon>
                    <span>创建留言箱</span>
                </div>
            </template>

            <el-form ref="messageFormRef" :model="messageForm" :rules="messageRules" label-width="100px"
                class="message-form">
                <el-form-item label="留言标题" prop="title">
                    <el-input v-model="messageForm.title" placeholder="请输入留言标题" style="width: 100%" />
                </el-form-item>

                <el-form-item label="主播寄语" prop="content">
                    <el-input v-model="messageForm.content" type="textarea" :rows="4" placeholder="请输入留言内容"
                        style="width: 100%" />
                </el-form-item>

                <el-form-item label="留言时间" prop="createTime">
                    <el-date-picker v-model="messageForm.createTime" type="datetime-local" placeholder="选择留言时间"
                        style="width: 100%" disabled/>
                </el-form-item>

                <el-form-item label="留言编号" prop="messageId">
                    <el-input v-model="messageForm.messageId" placeholder="请输入留言编号" style="width: 100%" disabled/>
                </el-form-item>

                <el-form-item label="公开状态" prop="isPublic">
                    <el-radio-group v-model="messageForm.isPublic">
                        <el-radio :label="true">公开</el-radio>
                        <el-radio :label="false">私密</el-radio>
                    </el-radio-group>
                </el-form-item>

                <el-form-item style="text-align: center">
                    <el-button type="primary" @click="submitForm">提交留言</el-button>
                    <el-button @click="resetForm">重置</el-button>
                </el-form-item>
            </el-form>

            <!-- 加载状态 -->
            <div v-if="loading" class="loading-container">
                <el-loading-spinner size="large" />
                <p>提交中...</p>
            </div>

            <!-- 成功提示 -->
            <div v-if="success" class="success-container">
                <el-icon class="success-icon">
                    <CircleCheckFilled />
                </el-icon>
                <p>留言创建成功！</p>
                <el-button type="primary" @click="goBack">返回列表</el-button>
            </div>
        </el-card>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Message, CircleCheckFilled } from '@element-plus/icons-vue';
import axios from 'axios';
import { useRouter } from 'vue-router';
const router = useRouter();
// 表单引用
const messageFormRef = ref(null);

// 表单数据
const messageForm = ref({
    title: '',
    content: '',
    createTime: new Date(),
    messageId: '',
    isPublic: true
});

// 表单验证规则
const messageRules = ref({
    title: [
        { required: true, message: '请输入留言标题', trigger: 'blur' },
        { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
    ],
    content: [
        { required: true, message: '请输入留言内容', trigger: 'blur' },
        { min: 5, message: '内容不能少于5个字符', trigger: 'blur' }
    ],
    messageId: [
        { pattern: /^[A-Za-z0-9_-]{4,20}$/, message: '编号由4-20位字母、数字、下划线或短横线组成', trigger: 'blur' }
    ]
});

// 状态管理
const loading = ref(false);
const success = ref(false);

// 提交表单
const submitForm = async () => {
    if (!messageFormRef.value) return;

    // 验证表单
    await messageFormRef.value.validate(async (valid) => {
        if (valid) {
            // 准备提交数据
            const formData = {
                ...messageForm.value,
                createTime: messageForm.value.createTime.toISOString()
            };

            try {
                loading.value = true;
                success.value = false;

                // 发送POST请求到后端
                const response = await axios.post('/adminapi/message/add', formData);

                if (response.data.code === 200) {
                    ElMessage.success('留言创建成功');
                    resetForm()
                    success.value = true;
                } else {
                    ElMessage.error(response.data.message || '创建失败，请重试');
                }
            } catch (error) {
                console.error('创建留言失败:', error);
                ElMessage.error('网络错误，请检查您的网络连接');
            } finally {
                loading.value = false;
            }
        } else {
            // console.log('表单验证失败');
            return false;
        }
    });
};

// 重置表单
const resetForm = () => {
    if (messageFormRef.value) {
        messageFormRef.value.resetFields();
        messageForm.value.isPublic = true;
        messageForm.value.createTime = new Date();
    }
    messageForm.value.messageId = `msg_${Date.now().toString()}`;
};

// 返回列表
const goBack = () => {
    // 这里可以添加返回列表的逻辑
    ElMessage.info('返回留言列表');
    router.push('/admin/message/list');
};

onMounted(() => {
    // 生成随机留言编号（可选）
    messageForm.value.messageId = `msg_${Date.now().toString()}`;
});
</script>

<style lang="scss" scoped>
.message-create-container {
    padding: 30px;
    max-width: 800px;
    margin: 0 auto;

    .message-card {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }
    }

    .card-header {
        display: flex;
        align-items: center;
        color: #303133;
        font-size: 18px;
        font-weight: 500;

        .el-icon {
            margin-right: 10px;
            color: #409eff;
        }
    }

    .message-form {
        padding: 20px 0;

        .el-form-item {
            margin-bottom: 20px;
        }
    }

    .loading-container,
    .success-container {
        padding: 40px 0;
        text-align: center;
    }

    .loading-container {
        .el-loading-spinner {
            color: #409eff;
            margin-bottom: 15px;
        }

        p {
            color: #606266;
        }
    }

    .success-container {
        .success-icon {
            font-size: 48px;
            color: #67c23a;
            margin-bottom: 15px;
        }

        p {
            color: #606266;
            margin-bottom: 20px;
        }
    }
}
</style>